// Renders the toast queue. Mounted once, at the top of the application.

import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import clsx from 'clsx';
import { useToastStore, type ToastTone } from '../../stores/toastStore';

const TONE_STYLES: Record<ToastTone, { box: string; icon: typeof CheckCircle2; iconColor: string }> = {
  success: {
    box: 'border-butcher-green/30 bg-white',
    icon: CheckCircle2,
    iconColor: 'text-butcher-green',
  },
  error: {
    box: 'border-burgundy-red/30 bg-white',
    icon: AlertCircle,
    iconColor: 'text-burgundy-red',
  },
  info: {
    box: 'border-line-strong bg-white',
    icon: Info,
    iconColor: 'text-ink-muted',
  },
};

export function Toaster() {
  const toasts = useToastStore((state) => state.toasts);
  const dismiss = useToastStore((state) => state.dismiss);

  if (toasts.length === 0) return null;

  return (
    <div
      // `polite` so a screen reader finishes its sentence before announcing,
      // rather than interrupting someone mid-task.
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed bottom-4 end-4 z-[100] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
    >
      {toasts.map((item) => {
        const style = TONE_STYLES[item.tone];
        const Icon = style.icon;

        return (
          <div
            key={item.id}
            role={item.tone === 'error' ? 'alert' : 'status'}
            className={clsx(
              'pointer-events-auto flex animate-fade-in items-start gap-3 rounded-card border p-3.5 shadow-raised',
              style.box,
            )}
          >
            <Icon className={clsx('mt-0.5 h-5 w-5 shrink-0', style.iconColor)} aria-hidden="true" />

            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-ink">{item.message}</p>
              {item.detail ? <p className="mt-0.5 text-xs text-ink-muted">{item.detail}</p> : null}
            </div>

            <button
              type="button"
              onClick={() => dismiss(item.id)}
              className="-me-1 -mt-1 rounded p-1 text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
